import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Maximize2, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { links, workItems } from "../../data/portfolio";
import { media } from "../../data/media";
import { useVimeoMeta } from "../../hooks/useVimeoMeta";
import { lockScroll } from "../../utils/scrollLock";
import { aspectLabels, kindFromAspect, viewerFrames } from "../../utils/work";
import { vimeoPageUrl, vimeoPlayerUrl } from "../../utils/vimeo";
import type { WorkItem } from "../../types/work";
type ProjectViewerProps = {
  item: WorkItem | null;
  onClose: () => void;
  onNavigate: (item: WorkItem) => void;
  /** Sequence the prev/next controls walk. Defaults to the homepage selection. */
  items?: WorkItem[];
};
function timecode(seconds: number) {
  if (!Number.isFinite(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}
export function ProjectViewer({
  item,
  onClose,
  onNavigate,
  items = workItems
}: ProjectViewerProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const activeIndex = item ? items.findIndex(entry => entry.id === item.id) : -1;
  const meta = useVimeoMeta(item?.vimeoId);
  const aspect = item?.aspect ?? meta.aspect ?? "landscape";
  const title = item?.title ?? meta.title ?? "Untitled film";
  const kind = item?.kind ?? kindFromAspect(aspect);
  const cover = item ? meta.thumbnail ?? item.posterUrl ?? (item.plate ? (media as Record<string, string>)[item.plate] : undefined) ?? media.archivePlate : "";
  const sourceUrl = item?.vimeoId ? vimeoPageUrl(item.vimeoId) : links.sourceArchive;
  const step = useCallback((direction: 1 | -1) => {
    if (activeIndex < 0 || items.length < 2) return;
    const next = items[(activeIndex + direction + items.length) % items.length];
    onNavigate(next);
  }, [activeIndex, items, onNavigate]);
  useEffect(() => {
    if (!item) return;
    setPlaying(true);
    setMuted(true);
    setProgress(0);
    setCurrent(0);
    setDuration(0);
  }, [item]);
  useEffect(() => {
    if (!item) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowRight") {
        step(1);
        return;
      }
      if (event.key === "ArrowLeft") {
        step(-1);
        return;
      }
      if (event.key === " " && item.videoUrl) {
        event.preventDefault();
        setPlaying(value => !value);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    const releaseScroll = lockScroll();
    window.addEventListener("keydown", onKeyDown);
    return () => {
      releaseScroll();
      window.removeEventListener("keydown", onKeyDown);
      previousFocus.current?.focus();
    };
  }, [item, onClose, step]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) void video.play().catch(() => setPlaying(false));else video.pause();
  }, [playing, item]);
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = muted;
  }, [muted, item]);
  const onScrub = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    const value = Number(event.target.value);
    setProgress(value);
    if (video && Number.isFinite(video.duration)) {
      video.currentTime = value / 100 * video.duration;
    }
  };
  const requestFullscreen = () => {
    const target = videoRef.current ?? shellRef.current;
    void target?.requestFullscreen?.().catch(() => undefined);
  };
  return <AnimatePresence data-id="element-461" data-mp="22731i1mwn4zo">
      {item && <motion.div ref={dialogRef} className="fixed inset-0 z-[95] flex flex-col bg-[#040606]/98 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={`${title}, player`} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.32,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-462" data-mp="2284g2g34q9m5">
          <header className="flex min-h-[72px] shrink-0 items-center justify-between gap-6 border-b border-white/10 px-5 md:px-8" data-id="element-463" data-mp="3hpr6nikqzari">
            <div className="min-w-0" data-id="element-464" data-mp="e8sfypmswo42">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-465" data-mp="e7qxw17db1cu">
                {kind}{item.index ? ` / ${item.index}` : ""} · {aspectLabels[aspect]}
              </p>
              <p className="mt-1.5 truncate text-sm text-white" data-id="element-466" data-mp="4hxn32f1uq9n">{title}</p>
            </div>
            <button type="button" onClick={onClose} autoFocus data-cursor="Close" className="flex min-h-11 shrink-0 items-center gap-3 px-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-467" data-mp="3vo8r97fcw757">
              Close <X className="h-4 w-4" data-id="element-468" data-mp="2re6zg9wg24b7" />
            </button>
          </header>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 py-6 md:px-16" data-id="element-469" data-mp="2crdtvwcq8cw7">
            <button type="button" onClick={() => step(-1)} aria-label="Previous project" data-cursor="Prev" className="absolute left-2 z-10 hidden h-12 w-12 place-items-center border border-white/15 text-white/70 transition-colors hover:border-[#54f6c6] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] md:grid" data-id="element-470" data-mp="2hox66qpv6fli">
              <ArrowLeft className="h-4 w-4" data-id="element-471" data-mp="1yr2z4i8aqe5r" />
            </button>

            <AnimatePresence mode="wait" data-id="element-472" data-mp="3o6smknxwkili">
              <motion.div key={item.id} ref={shellRef} initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.99
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className={`relative overflow-hidden rounded-[20px] border border-white/10 bg-black ${viewerFrames[aspect]}`} data-id="element-473" data-mp="3o3y617i0fhxd">
                {item.vimeoId ? <iframe src={vimeoPlayerUrl(item.vimeoId)} title={`${title}, video player`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="h-full w-full border-0" data-id="element-474" data-mp="1meijaoyw40pb" /> : item.videoUrl ? <video ref={videoRef} src={item.videoUrl} poster={cover} className="h-full w-full object-contain" playsInline loop muted={muted} autoPlay onLoadedMetadata={event => setDuration(event.currentTarget.duration)} onTimeUpdate={event => {
              const video = event.currentTarget;
              setCurrent(video.currentTime);
              if (Number.isFinite(video.duration) && video.duration > 0) {
                setProgress(video.currentTime / video.duration * 100);
              }
            }} data-id="element-475" data-mp="2584k420b6c6m" /> : <div className="relative h-full w-full" data-id="element-476" data-mp="376aqhzegbo5t">
                    <img src={cover} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-45" data-id="element-477" data-mp="3c42q7flc8ekk" />
                    <div className="absolute inset-0 film-lines opacity-40" data-id="element-478" data-mp="3g6fstv4sfwd4" />
                    <div className="absolute inset-0 grid place-items-center px-8 text-center" data-id="element-479" data-mp="ren24sdi73ym">
                      <div data-id="element-480" data-mp="vfossktcu7oz">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-481" data-mp="vex9a8d180f2">{aspectLabels[aspect]} frame reserved</p>
                        <p className="mx-auto mt-5 max-w-md text-sm leading-7 text-white/65 md:text-[15px]" data-id="element-482" data-mp="2g03spvxukr8d">
                          The source portfolio doesn’t expose a direct file for this entry, so nothing has been substituted. Open the verified archive to watch it.
                        </p>
                        <a href={links.sourceArchive} target="_blank" rel="noreferrer" data-cursor="Open" className="mt-8 inline-flex min-h-11 items-center gap-3 bg-[#54f6c6] px-5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#05100d] transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-483" data-mp="224rw1j04sqot">
                          Open verified archive <ExternalLink className="h-3.5 w-3.5" data-id="element-484" data-mp="3dlhw0kdyom9z" />
                        </a>
                      </div>
                    </div>
                  </div>}
                <span aria-hidden="true" className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/80 backdrop-blur-sm" data-id="element-485" data-mp="3i1zgpzhiya8w">
                  {aspectLabels[aspect]}
                </span>
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => step(1)} aria-label="Next project" data-cursor="Next" className="absolute right-2 z-10 hidden h-12 w-12 place-items-center border border-white/15 text-white/70 transition-colors hover:border-[#54f6c6] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6] md:grid" data-id="element-486" data-mp="33dd4w7r3w0mf">
              <ArrowRight className="h-4 w-4" data-id="element-487" data-mp="2kjpomsdamb7g" />
            </button>
          </div>

          <footer className="shrink-0 border-t border-white/10 px-5 py-4 md:px-8" data-id="element-488" data-mp="2fkwlntvmu2lj">
            {!item.videoUrl && !item.vimeoId && <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-489" data-mp="zvey9znmoajs">
                Transport controls activate once this frame has a source file. Open the verified archive to watch it.
              </p>}

            {item.vimeoId ? <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-490" data-mp="1esxu1lyq3o0q">
                <span className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/55" data-id="element-491" data-mp="1eqteg6rgp0km">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#54f6c6] dot-blink" data-id="element-492" data-mp="14uqcp20iv3y7" />
                  Streaming from Vimeo · play, sound, and fullscreen sit on the player
                </span>
                <a href={sourceUrl} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 px-3 font-mono text-[11px] uppercase tracking-[0.14em] text-white/75 transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-493" data-mp="3pkfrjf186pqw">
                  Watch on Vimeo <ExternalLink className="h-3.5 w-3.5" data-id="element-494" data-mp="31o2q70gl1wzo" />
                </a>
              </div> : <div className="flex flex-wrap items-center gap-4" data-id="element-495" data-mp="2wogckpwueeqc">
              <div className="flex items-center gap-2" data-id="element-496" data-mp="2rsitibu72u9v">
                <button type="button" onClick={() => setPlaying(value => !value)} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label={playing ? "Pause" : "Play"} data-cursor={playing ? "Pause" : "Play"} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-497" data-mp="2msd12tg3p3qq">
                  {playing ? <Pause className="h-4 w-4" data-id="element-498" data-mp="ohct6onozuo8" /> : <Play className="h-4 w-4 fill-current" data-id="element-499" data-mp="ogkzjjdno70l" />}
                </button>
                <button type="button" onClick={() => setMuted(value => !value)} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label={muted ? "Sound on" : "Sound off"} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-500" data-mp="tbqu16l4iu0a">
                  {muted ? <VolumeX className="h-4 w-4" data-id="element-501" data-mp="1m0e92kfa4n5e" /> : <Volume2 className="h-4 w-4" data-id="element-502" data-mp="1pzn21n2hwgzw" />}
                </button>
              </div>

              <span className="font-mono text-[11px] tracking-[0.12em] text-white/60" data-id="element-503" data-mp="1oiho6beh8qit">{timecode(current)}</span>
              <input type="range" min={0} max={100} step={0.1} value={progress} onChange={onScrub} disabled={!item.videoUrl} aria-label="Seek" className="viewer-scrub h-1 min-w-[140px] flex-1 appearance-none bg-white/15 disabled:opacity-30" data-id="element-504" data-mp="1jk6cdv4mg5xy" />
              <span className="font-mono text-[11px] tracking-[0.12em] text-white/45" data-id="element-505" data-mp="22j285aw4osdw">{timecode(duration)}</span>

              <div className="flex items-center gap-2" data-id="element-506" data-mp="2h6dhd64u80au">
                <button type="button" onClick={requestFullscreen} disabled={!item.videoUrl} title={item.videoUrl ? undefined : "No source file attached to this frame yet"} aria-label="Fullscreen" className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-colors hover:border-[#54f6c6] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-507" data-mp="2h99jl6npxb53">
                  <Maximize2 className="h-4 w-4" data-id="element-508" data-mp="2vtc8ezzs8sp1" />
                </button>
                <a href={links.sourceArchive} target="_blank" rel="noreferrer" data-cursor="Open" className="flex min-h-11 items-center gap-2 px-3 font-mono text-[11px] uppercase tracking-[0.14em] text-white/75 transition-colors hover:text-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-509" data-mp="30sfdgkfriyx0">
                  Source <ExternalLink className="h-3.5 w-3.5" data-id="element-510" data-mp="2h5hc79mkg677" />
                </a>
              </div>
            </div>}

            {/* Touch-friendly stepper, since the side arrows are desktop-only. */}
            <div className="mt-4 flex items-center gap-2 md:hidden" data-id="element-511" data-mp="286m9w8mn7g96">
              <button type="button" onClick={() => step(-1)} aria-label="Previous project" className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full border border-white/15 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:border-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-512" data-mp="dpvwi46tq0tr">
                <ArrowLeft className="h-4 w-4" data-id="element-513" data-mp="slv31aeabsws" /> Prev
              </button>
              <button type="button" onClick={() => step(1)} aria-label="Next project" className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full border border-white/15 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:border-[#54f6c6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54f6c6]" data-id="element-514" data-mp="sjr12xzy53z5">
                Next <ArrowRight className="h-4 w-4" data-id="element-515" data-mp="2tar8bw5yrjqx" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45" data-id="element-516" data-mp="2y7zppujafjr4">
              <span data-id="element-517" data-mp="335t0z1zg73y9">{(item.tags ?? []).join(" · ")}</span>
              <span data-id="element-518" data-mp="37znned6ftx11">
                {String(activeIndex + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                <span className="hidden md:inline" data-id="element-519" data-mp="3bzxw3xlm71jl"> · ← → to move</span>
              </span>
            </div>
          </footer>
        </motion.div>}
    </AnimatePresence>;
}